/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  AlwaysLightMode,
  Button,
  Hidden,
  Link,
  ProductCard,
  Skeleton,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import type { Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import CollectionFilterChips from 'bundles/cds-labs/components/Modules/CollectionsModule/CollectionFilterChips';
import type { SpotlightCollectionsModuleProps } from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
import ModuleLayout, { MODULE_LAYOUTS, getModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import {
  TYPOGRAPHY_DARK_COLOR,
  TYPOGRAPHY_LIGHT_COLOR,
  getCustomBackgroundStyle,
  parseTextColorOverride,
  resolveIsDarkBackground,
} from 'bundles/cds-labs/components/Modules/utils/colors';
import { getIsSideBySideLayout } from 'bundles/cds-labs/components/Modules/utils/constants';
import OverflowCarousel from 'bundles/cds-labs/components/OverflowCarousel/OverflowCarousel';
import CompactLearningProductCard from 'bundles/product-card/components/CompactLearningProductCard';
import { useStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';

const DEFAULT_SPOTLIGHT_MAX_CARDS = 4;
const CARD_WIDTH_DESKTOP = 242;
const CARD_WIDTH_MOBILE = 150;

const descriptionLinkStyle = css`
  color: inherit !important;
`;

const DescriptionLink = ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <Link href={href} variant="standard" typographyVariant="inherit" css={descriptionLinkStyle}>
    {children}
  </Link>
);

/** Shared ReactMarkdown renderers for spotlight description links. */
export const spotlightMarkdownRenderers = {
  link: DescriptionLink,
};

const styles = {
  // Prevents custom background from overriding headline-as-link color.
  inheritColor: css`
    color: inherit !important;
  `,
  // CSS-only responsive toggle: show headline-as-link at <lg, heading+CTA at lg+.
  // CSS-only responsive toggle to avoid hydration flicker.
  showBelowLg: css`
    ${breakpoints.up('lg')} {
      display: none;
    }
  `,
  showAtLg: css`
    display: none;
    ${breakpoints.up('lg')} {
      display: revert;
    }
  `,
  spotlightContentCenter: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    align-items: flex-start;

    ${breakpoints.down('md')} {
      height: auto;
      justify-content: flex-start;
    }
  `,
  // When renderPrimaryCtaOnMobile is true, places the CTA beside the headline at tablet widths.
  spotlightContentRowAtTablet: css`
    ${breakpoints.down('md')} {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  `,
  // Forces stacking at <=md regardless of ModuleLayout's Grid column config.
  spotlightForceStackAtMd: css`
    ${breakpoints.down('md')} {
      && {
        max-width: 100%;
        flex-basis: 100%;
      }
    }
  `,
  // Overrides Grid's md={3}/md={9} (25/75) to match 1fr/4fr (20/80) ratio at lg+.
  spotlightContentPanelLgWidth: css`
    ${breakpoints.up('lg')} {
      && {
        flex-basis: 20%;
        max-width: 20%;
      }
    }
  `,
  spotlightImagePanelLgWidth: css`
    ${breakpoints.up('lg')} {
      && {
        flex-basis: 80%;
        max-width: 80%;
      }
    }
  `,
  spotlightCarousel: (entityCount: number) => css`
    display: grid;
    grid-template-columns: repeat(${Math.min(entityCount, DEFAULT_SPOTLIGHT_MAX_CARDS)}, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;

    ${breakpoints.down('xs')} {
      grid-template-columns: repeat(
        ${Math.min(entityCount, DEFAULT_SPOTLIGHT_MAX_CARDS)},
        minmax(${CARD_WIDTH_MOBILE}px, 1fr)
      );
      width: auto;
    }

    ${entityCount > DEFAULT_SPOTLIGHT_MAX_CARDS &&
    css`
      grid-template-columns: repeat(${entityCount}, ${CARD_WIDTH_DESKTOP}px);
      grid-template-rows: 1fr;
      width: max-content;

      ${breakpoints.down('xs')} {
        grid-template-columns: repeat(${entityCount}, minmax(${CARD_WIDTH_MOBILE}px, 1fr));
      }
    `}

    .cds-ProductCard-grid {
      min-width: unset !important;
    }

    .cds-Avatar-base {
      width: 12px !important;
      height: 12px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cds-ProductCard-footer {
      gap: 0;
    }
  `,
  spotlightContentPanel: css`
    min-width: 0;
    overflow-wrap: break-word;

    /* At md (stacked), remove Grid spacing's bottom padding to tighten headline-to-cards gap. */
    ${breakpoints.down('md')} {
      && {
        padding-bottom: 0;
      }
    }
  `,
  spotlightImagePanelBase: css`
    min-width: 0;

    > * {
      min-width: 0;
    }

    button {
      color: var(--cds-color-neutral-primary);

      &:hover {
        color: var(--cds-color-interactive-primary);
      }
    }
  `,
  spotlightImagePanelFlat: css`
    ${breakpoints.up('lg')} {
      && {
        padding-left: var(--cds-spacing-100);
        padding-right: var(--cds-spacing-100);
      }
    }
  `,
  // Passed to OverflowCarousel's css prop for flat panels — remove right padding.
  spotlightCarouselContentFlatOverride: css`
    && {
      padding-right: 0;
    }
  `,
  spotlightRoundedContainer: css`
    border-radius: var(--cds-border-radius-300);
  `,
  spotlightInvertedText: css`
    color: var(--cds-color-neutral-primary-invert);
  `,
  spotlightDarkText: css`
    color: var(--cds-color-neutral-primary);
  `,
  spotlightCarouselContainer: (showCarouselNavigation: boolean) => css`
    ${showCarouselNavigation &&
    css`
      overflow: hidden;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
      margin: calc(-1 * var(--cds-spacing-150)) calc(-1 * var(--cds-spacing-200));

      /* Override carousel navigation dot colors for dark backgrounds */
      ul {
        --cds-color-neutral-primary-weak: var(--cds-color-grey-200);
        --cds-color-neutral-stroke-primary: var(--cds-color-neutral-primary-invert);
      }
    `}
  `,
  largerFontForExperimentalCards: css`
    & p.cds-ProductCard-partnerNames {
      font-size: 14px;
    }
  `,
  largerGapForBorderlessCards: css`
    & div[role='list'] {
      gap: var(--cds-spacing-300);
    }
  `,
  spotlightCarouselLoading: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  description: css`
    margin-top: var(--cds-spacing-50);

    p {
      margin: 0;
    }
  `,
  alignCta: css`
    margin-top: var(--cds-spacing-150);
  `,
  button: css`
    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,
  ghostCta: css`
    &:hover,
    &:focus-visible {
      background-color: transparent;
      text-decoration: underline;
    }
  `,
  customContentPanelStyles: {
    default: (hasFilters: boolean) => css`
      && {
        ${hasFilters
          ? `padding-bottom: var(--cds-spacing-100);

        ${breakpoints.down('xs')} {
          padding-bottom: 0;
        }`
          : `padding-bottom: var(--cds-spacing-200);

          ${breakpoints.down('xs')} {
          padding-bottom: var(--cds-spacing-100);
        }
        `}
      }
    `,
    sideBySideLayout: css`
      && {
        padding-bottom: 0;
      }
    `,
  },
  customImagePanelStyles: {
    default: css`
      ${breakpoints.down('md')} {
        && {
          padding-top: 0;
        }
      }
    `,
    sideBySideLayout: css`
      && {
        padding-top: var(--cds-spacing-150);
      }
    `,
  },
};

function SpotlightCollectionsModule(props: SpotlightCollectionsModuleProps) {
  const {
    filters,
    layout,
    onFilterChange,
    backgroundColor,
    cta,
    description,
    headline,
    headlineComponent,
    collections = [],
    isCollectionsLoading,
    currentCollectionSetIndex = 0,
    numOfEntriesPerCollection,
    customCtaButton,
    customChipStyles,
    alwaysShowChipFilters,
    eventingData,
    customHeadline,
    eyebrow,
    customBackgroundColor,
    textColorOverride,
    dataTestId,
    renderPrimaryCtaOnMobile,
  } = props;

  const layoutConfig = getModuleLayoutConfig(props);
  const isXsOrLess = useMediaQuery(breakpoints.down('xs'));
  const isSmOrLess = useMediaQuery(breakpoints.down('sm'));
  const { statusTagRedesignVariant, isBorderlessCard } = useStatusTagRedesignExperimentVariant();
  const isStatusTagRedesign = statusTagRedesignVariant !== 'control';

  // Desktop layout always — stacking at <=md handled by CSS (spotlightForceStackAtMd).
  const effectiveLayout = getIsSideBySideLayout(layout) ? layout : MODULE_LAYOUTS.text3image9;

  const isThreeByNineLayout =
    effectiveLayout === MODULE_LAYOUTS.text3image9 || effectiveLayout === MODULE_LAYOUTS.image9text3;
  const isSideBySideLayout = getIsSideBySideLayout(effectiveLayout);

  const parsedTextColorOverride = parseTextColorOverride(textColorOverride);
  const isInverted = resolveIsDarkBackground({
    backgroundColor,
    customBackgroundColor,
    textColorOverride: parsedTextColorOverride,
  });

  // At mobile, spotlight drops rounded corners for edge-to-edge background
  const effectiveShowRoundedCorners = isXsOrLess ? false : !!layoutConfig.showRoundedCorners;

  // Non-rounded: full-width background via ModuleLayout's outer div.
  // Rounded: background on the inner PageGridContainer via customPageContainerStyles.
  const needsFullWidthBg = !!customBackgroundColor && !effectiveShowRoundedCorners;

  const textColorStyle = isInverted ? styles.spotlightInvertedText : styles.spotlightDarkText;
  if (customBackgroundColor && !needsFullWidthBg) {
    const customBgParsed = getCustomBackgroundStyle(customBackgroundColor);
    if (customBgParsed) {
      const textColor = isInverted ? TYPOGRAPHY_LIGHT_COLOR : TYPOGRAPHY_DARK_COLOR;
      const customBgCss = css`
        ${customBgParsed.property === 'background-image'
          ? `background-image: ${customBgParsed.value}`
          : `background-color: ${customBgParsed.value}`};
        color: ${textColor};
      `;
      layoutConfig.customPageContainerStyles = [...(layoutConfig.customPageContainerStyles ?? []), customBgCss];
    }
  } else if (needsFullWidthBg) {
    layoutConfig.backgroundColor = customBackgroundColor;
    layoutConfig.customPageContainerStyles = [...(layoutConfig.customPageContainerStyles ?? []), textColorStyle];
  }

  if (!needsFullWidthBg) {
    layoutConfig.customPageContainerStyles = [
      ...(layoutConfig.customPageContainerStyles ?? []),
      styles.spotlightRoundedContainer,
    ];
  }

  let button: React.ReactNode;
  if (customCtaButton) {
    button = <div css={styles.alignCta}>{customCtaButton}</div>;
  } else {
    let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
    if (cta?.variant === 'ghost') {
      variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
    }
    button =
      cta?.text && cta?.target ? (
        <div css={styles.alignCta}>
          <Button
            component="a"
            variant={variantOfCta}
            size={cta.size ?? undefined}
            icon={<ArrowNextIcon />}
            href={cta.target}
            onClick={cta.onClick}
            css={cta.variant === 'ghost' ? styles.ghostCta : styles.button}
            fullWidth={isXsOrLess}
            edgeAlign={isXsOrLess ? undefined : 'start'}
          >
            {cta.text}
          </Button>
        </div>
      ) : null;
  }

  // Filter chip computation
  const shouldRenderImagesForFilters =
    filters?.reduce((acc, filter) => {
      return filter.filterImage ? acc + 1 : acc;
    }, 0) === filters?.length;
  const hasFilters = !!filters && (filters.length > 1 || !!alwaysShowChipFilters);

  const renderFilterChips = () => {
    if (!hasFilters || !filters) return null;
    return (
      <CollectionFilterChips
        filters={filters}
        currentCollectionSetIndex={currentCollectionSetIndex}
        isSideBySideLayout={isSideBySideLayout}
        shouldRenderImagesForFilters={!!shouldRenderImagesForFilters}
        customChipStyles={customChipStyles}
        onFilterChange={onFilterChange}
        collections={collections}
        eventingData={eventingData}
      />
    );
  };

  const getCustomPanelStyles = (panelType: 'content' | 'image') => {
    if (!isSideBySideLayout) {
      if (panelType === 'content') {
        return [styles.customContentPanelStyles.default(hasFilters)];
      } else {
        return [styles.customImagePanelStyles.default];
      }
    }

    const styleSet = panelType === 'content' ? styles.customContentPanelStyles : styles.customImagePanelStyles;
    if (isThreeByNineLayout && isSmOrLess) {
      return [styleSet.sideBySideLayout];
    }

    return [];
  };

  const renderImage = () => {
    const loadingCardCount = numOfEntriesPerCollection ?? DEFAULT_SPOTLIGHT_MAX_CARDS;
    const collection = collections[0];

    if (isCollectionsLoading || !collection) {
      return (
        <div>
          {renderFilterChips()}
          <div css={styles.spotlightCarouselLoading}>
            {Array.from({ length: loadingCardCount }, (_, i) => (
              <ProductCard key={i} variant="grid" productType="role" loading />
            ))}
          </div>
        </div>
      );
    }

    const filteredEntities = collection.entities.filter((e) => !!e.name);
    const entities =
      numOfEntriesPerCollection != null ? filteredEntities.slice(0, numOfEntriesPerCollection) : filteredEntities;
    const cardCollection = {
      ...collection,
      title: headline ?? '',
      collectionTrackingData: collection.collectionTrackingData ?? { id: '' },
    };

    const showCarouselNavigation = entities.length > DEFAULT_SPOTLIGHT_MAX_CARDS;
    return (
      <div>
        {renderFilterChips()}
        <div
          css={[
            styles.spotlightCarouselContainer(showCarouselNavigation),
            isStatusTagRedesign && styles.largerFontForExperimentalCards,
            isBorderlessCard && styles.largerGapForBorderlessCards,
          ]}
        >
          <OverflowCarousel
            css={[
              styles.spotlightCarousel(entities.length),
              !effectiveShowRoundedCorners && styles.spotlightCarouselContentFlatOverride,
            ]}
            shouldAllowSpaceForHoverItems
          >
            {entities.map((entity, index) => (
              <CompactLearningProductCard
                entity={entity}
                entityIndex={index}
                collectionIndex={currentCollectionSetIndex}
                collection={cardCollection}
                key={entity.id}
              />
            ))}
          </OverflowCarousel>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isCollectionsLoading && !headline && !customHeadline) {
      return (
        <div>
          <Skeleton data-testid="skeleton" width="70%" height={40} />
        </div>
      );
    }

    // Both variants rendered; CSS toggles visibility. renderPrimaryCtaOnMobile bypasses the toggle.
    const headlineAsLink = (
      <Link
        href={cta?.target ?? undefined}
        variant="quiet"
        icon={<ArrowNextIcon size="medium" />}
        iconPosition="after"
        css={styles.inheritColor}
        onClick={cta?.onClick}
        typographyVariant="subtitleLarge"
        aria-label={cta?.text ? `${cta.text} - ${headline}` : headline ?? undefined}
      >
        {headline}
      </Link>
    );

    const headlineAsHeading = customHeadline || (
      <Typography2 variant="subtitleLarge" component={headlineComponent ?? 'h2'} color="inherit">
        {headline}
      </Typography2>
    );

    const ctaBlock = renderPrimaryCtaOnMobile ? button : <Hidden smDown>{button}</Hidden>;

    const descriptionBlock = description ? (
      <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.description}>
        {description}
      </Typography2>
    ) : null;

    const eyebrowBlock = eyebrow?.image && (
      <div css={styles.eyebrowSpacing}>
        <Eyebrow image={eyebrow.image} />
      </div>
    );

    return (
      <div css={[styles.spotlightContentCenter, renderPrimaryCtaOnMobile && styles.spotlightContentRowAtTablet]}>
        {renderPrimaryCtaOnMobile ? (
          <>
            {/* Grouped with the headline/description so it stays stacked above the text instead of
                becoming a row-sibling of the CTA when spotlightContentRowAtTablet switches to flex-row. */}
            <div>
              {eyebrowBlock}
              {headlineAsHeading}
              {descriptionBlock}
            </div>
            {ctaBlock}
          </>
        ) : (
          <>
            {eyebrowBlock}
            <div css={styles.showBelowLg}>{headlineAsLink}</div>
            <div css={styles.showAtLg}>{headlineAsHeading}</div>
            {descriptionBlock}
            <div css={styles.showAtLg}>{ctaBlock}</div>
          </>
        )}
      </div>
    );
  };

  const moduleLayout = (
    <ModuleLayout
      renderContent={renderContent}
      renderImage={renderImage}
      dataTestId={dataTestId}
      assembly={{
        ...layoutConfig,
        showRoundedCorners: effectiveShowRoundedCorners,
        layout: effectiveLayout,
        customContentPanelStyles: [
          ...getCustomPanelStyles('content'),
          styles.spotlightContentPanel,
          styles.spotlightForceStackAtMd,
          styles.spotlightContentPanelLgWidth,
        ],
        customImagePanelStyles: [
          ...getCustomPanelStyles('image'),
          styles.spotlightImagePanelBase,
          styles.spotlightForceStackAtMd,
          styles.spotlightImagePanelLgWidth,
          ...(!effectiveShowRoundedCorners ? [styles.spotlightImagePanelFlat] : []),
        ],
        customPageContainerStyles: [
          ...(layoutConfig.customPageContainerStyles ?? []),
          ...(isInverted && !customBackgroundColor ? [styles.spotlightInvertedText] : []),
        ],
      }}
    />
  );

  return customBackgroundColor ? <AlwaysLightMode>{moduleLayout}</AlwaysLightMode> : moduleLayout;
}

export default SpotlightCollectionsModule;
